import { ENUM_LABELS, type ImportRunStatus, type ImportRunView } from '#shared/types';

/** The wizard's four steps, in the order they are shown and walked. */
export const WIZARD_STEPS = ['upload', 'map', 'check', 'commit'] as const;

export type WizardStep = (typeof WIZARD_STEPS)[number];

export const WIZARD_STEP_LABELS: Record<WizardStep, string> = {
  upload: 'Upload',
  map: 'Map the columns',
  check: 'Check',
  commit: 'Import',
};

type WizardAction = { type: 'go'; step: WizardStep } | { type: 'back' } | { type: 'forward' };

export function stepIndex(step: WizardStep): number {
  return WIZARD_STEPS.indexOf(step);
}

const PREVIOUS: Record<WizardStep, WizardStep> = {
  upload: 'map',
  map: 'map',
  check: 'map',
  commit: 'check',
};

const NEXT: Record<WizardStep, WizardStep> = {
  upload: 'map',
  map: 'check',
  check: 'commit',
  commit: 'commit',
};

/**
 * The step the operator has chosen. `back` never returns to the upload step —
 * the file is already read — and `forward` stops at the commit step.
 */
export function wizardReducer(chosen: WizardStep, action: WizardAction): WizardStep {
  switch (action.type) {
    case 'go':
      return action.step;
    case 'back':
      return PREVIOUS[chosen];
    case 'forward':
      return NEXT[chosen];
  }
}

/**
 * What the `:id` segment names: a new import, an existing run, or nothing that
 * exists — a hand-edited URL such as `/imports/abc`, which must not become a
 * request for run `NaN`.
 */
type RunRoute = { kind: 'new' } | { kind: 'run'; runId: number } | { kind: 'invalid' };

export function parseRunRoute(id: string | undefined): RunRoute {
  if (id === undefined || id === 'new') return { kind: 'new' };
  const runId = Number(id);
  return Number.isInteger(runId) && runId > 0 ? { kind: 'run', runId } : { kind: 'invalid' };
}

export function initialStep(runId: number | null): WizardStep {
  return runId === null ? 'upload' : 'map';
}

/** The write has begun (or finished): there is nothing left to map or check. */
export function isWritten(status: ImportRunStatus | undefined): boolean {
  return status === 'committing' || status === 'committed' || status === 'partially_committed';
}

/** No step can be revisited: the write is running or has fully landed. */
export function isLocked(status: ImportRunStatus | undefined): boolean {
  return status === 'committing' || status === 'committed';
}

/**
 * The step actually shown. A run that has already been written has nothing
 * left to map or check, so the last step is *derived* rather than forced with
 * an effect — an effect that pushes the operator forward renders the wrong step
 * first and then corrects it, which on a screen with a Back button reads as a
 * glitch.
 */
export function visibleStep(chosen: WizardStep, status: ImportRunStatus | undefined): WizardStep {
  return isWritten(status) ? 'commit' : chosen;
}

export function wizardTitle(
  runId: number | null,
  run: Pick<ImportRunView, 'file_name'> | null,
): string {
  return runId === null ? 'Import a spreadsheet' : `Import ${run?.file_name ?? ''}`;
}

export function wizardSubtitle(run: Pick<ImportRunView, 'entity' | 'status'> | null): string {
  return run
    ? `${ENUM_LABELS.importEntity[run.entity]} · ${ENUM_LABELS.importRunStatus[run.status]}`
    : 'Read a sheet from the lab’s workbooks into the system';
}
